import {FC, useState} from 'react'
import {useIntl} from 'react-intl'
import {IOfferModel} from '../cors/_models'
import {DeliveryPathChip, WinningStar, AdminOverrideDot} from '../shared'
import {toCatalogImageUrl} from '../../../../_metronic/helpers'
import {displayQuantity, isShopScoped} from '../../common/stockScope'

/**
 * One row in the My Offers table. Stateless: renders props, calls back
 * to the parent for any mutation (the parent owns the API call + the
 * row-level optimistic refresh).
 *
 * UX cues that distinguish this from the legacy product list:
 *   ★ winning star  — yellow when this offer is currently winning the
 *                     buy box; grey/empty otherwise.
 *   admin override  — small red dot next to the price with a tooltip.
 *   delivery chip   — colour-coded path so the supplier knows whether
 *                     their stock ships from FC, via Dumyah pickup,
 *                     or via their own delivery to FC.
 */

type IProps = {
  offer: IOfferModel
  busy: boolean
  onEdit: (offer: IOfferModel) => void
  onPause: (offer: IOfferModel) => Promise<void>
  onActivate: (offer: IOfferModel) => Promise<void>
}

const fmtMoney = (v: string | number | null | undefined): string => {
  if (v === null || v === undefined || v === '') return '—'
  const n = typeof v === 'number' ? v : parseFloat(v)
  return Number.isFinite(n) ? n.toFixed(2) : '—'
}

const OfferTableRow: FC<IProps> = ({offer, busy, onEdit, onPause, onActivate}) => {
  const {formatMessage} = useIntl()
  const [localBusy, setLocalBusy] = useState(false)

  const isEnabled = offer.is_active === 1
  // The shelf for a partner shop's account (stock_scope "shop"), at-supplier stock otherwise.
  const shopScoped = isShopScoped(offer)
  const stockQty = displayQuantity(offer)
  // Active = enabled, priced and in stock. An enabled offer that is not
  // active is out of the running, and the badge says which of the two it lacks.
  // This build can go live before the API does (staging deploys on every PR
  // push, production on merge); an API without is_competing gets the old
  // enabled/paused badges rather than "Not competing" on every row.
  const isActive = offer.is_competing === undefined ? isEnabled : offer.is_competing === 1
  const hasStock = shopScoped ? stockQty > 0 : stockQty + offer.warehouse_quantity > 0
  const isWinning = offer.is_winning_offer === 1

  const handlePause = async () => {
    setLocalBusy(true)
    try {
      await onPause(offer)
    } finally {
      setLocalBusy(false)
    }
  }

  const handleActivate = async () => {
    setLocalBusy(true)
    try {
      await onActivate(offer)
    } finally {
      setLocalBusy(false)
    }
  }

  const disabled = busy || localBusy

  return (
    <tr aria-busy={disabled}>
      {/* Winning star */}
      <td className="hsb-of-td-center" style={{width: 56}}>
        <WinningStar winning={isWinning} />
      </td>

      {/* Image + name (clicking opens edit drawer) */}
      <td>
        <button
          type="button"
          className="hsb-of-prod"
          onClick={() => onEdit(offer)}
          disabled={disabled}
          aria-label={formatMessage(
            {id: 'offers.row.edit_aria'},
            {name: offer.product_name ?? `#${offer.product_id}`},
          )}
        >
          {offer.image ? (
            <img
              src={toCatalogImageUrl(offer.image)}
              alt=""
              className="hsb-of-prod-img"
              loading="lazy"
            />
          ) : (
            <span className="hsb-of-prod-img hsb-of-prod-img--empty" aria-hidden="true" />
          )}
          <span className="hsb-of-prod-name">{offer.product_name ?? '—'}</span>
        </button>
      </td>

      {/* Barcode */}
      <td className="hsb-of-nowrap">{offer.barcode ?? '—'}</td>

      {/* Price (with admin-override flag) */}
      <td className="hsb-of-nowrap">
        <span className="hsb-of-money">{fmtMoney(offer.price)}</span>
        <AdminOverrideDot active={offer.admin_override_active} />
      </td>

      {/* Quantity (at-supplier stock) + FC stock subtotal */}
      <td className="hsb-of-nowrap">
        <span className={stockQty === 0 ? 'hsb-of-stock-zero' : ''}>
          {stockQty}
        </span>
        {!shopScoped && offer.warehouse_quantity > 0 ? (
          <span className="hsb-of-fc">
            ({offer.warehouse_quantity} {formatMessage({id: 'offers.row.in_fc'})})
          </span>
        ) : null}
      </td>

      {/* Delivery path chip */}
      <td>
        <DeliveryPathChip
          path={offer.effective_delivery_path}
          hours={offer.effective_delivery_hours}
        />
      </td>

      {/* Status badge */}
      <td>
        {isActive ? (
          <span className="hsb-of-status hsb-of-status--active">
            {formatMessage({id: 'offers.row.status_active'})}
          </span>
        ) : !isEnabled ? (
          <span className="hsb-of-status hsb-of-status--paused">
            {formatMessage({id: 'offers.row.status_paused'})}
          </span>
        ) : (
          <span
            className="hsb-of-status hsb-of-status--paused"
            title={formatMessage({
              id: hasStock
                ? 'offers.row.not_competing_unpriced'
                : 'offers.row.not_competing_out_of_stock',
            })}
          >
            {formatMessage({id: 'offers.row.status_not_competing'})}
          </span>
        )}
      </td>

      {/* Actions */}
      <td className="hsb-of-td-end">
        <div className="hsb-of-row-actions">
          <button
            type="button"
            className="hsb-of-act hsb-of-act--edit"
            onClick={() => onEdit(offer)}
            disabled={disabled}
            title={formatMessage({id: 'offers.row.edit'})}
            aria-label={formatMessage({id: 'offers.row.edit'})}
          >
            <span className="material-symbols-outlined">edit</span>
          </button>
          {isEnabled ? (
            <button
              type="button"
              className="hsb-of-act hsb-of-act--pause"
              onClick={handlePause}
              disabled={disabled}
              title={formatMessage({id: 'offers.row.pause'})}
              aria-label={formatMessage({id: 'offers.row.pause'})}
            >
              <span className="material-symbols-outlined">pause_circle</span>
            </button>
          ) : (
            <button
              type="button"
              className="hsb-of-act hsb-of-act--activate"
              onClick={handleActivate}
              disabled={disabled}
              title={formatMessage({id: 'offers.row.activate'})}
              aria-label={formatMessage({id: 'offers.row.activate'})}
            >
              <span className="material-symbols-outlined">play_circle</span>
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

export default OfferTableRow
